// RunnerGame.jsx
// "Duplicate Dash" — a tiny endless-runner played on a single <canvas>.
// One control: jump the incoming duplicate-lead stacks. No assets, no new
// dependencies, nothing auto-starts (prefers-reduced-motion friendly by
// construction — the animation loop only ever begins from a keypress,
// click, or tap). Lives inside WaitingRoom, but is self-contained enough to
// drop anywhere.
import React, { useEffect, useRef, useState } from "react";
import "./RunnerGame.css";

const STORAGE_KEY = "leadscrub.arcade.duplicateDash.best";

const GROUND_RATIO = 0.78; // ground line as a fraction of canvas height
const PLAYER_SIZE = 24;
const GRAVITY = 2200; // px/s^2
const JUMP_VELOCITY = -740; // px/s
const BASE_SPEED = 240; // px/s, ramps up the longer you survive
const SPEED_RAMP = 5.5; // px/s per second survived
const SPAWN_MIN_MS = 850;
const SPAWN_MAX_MS = 1500;
const SCORE_PER_SEC = 12;

function loadBest() {
	try {
		const raw = window.localStorage.getItem(STORAGE_KEY);
		const n = raw ? parseInt(raw, 10) : 0;
		return Number.isFinite(n) && n > 0 ? n : 0;
	} catch {
		return 0; // localStorage unavailable (private mode, quota, disabled) — no big deal
	}
}

function saveBest(value) {
	try {
		window.localStorage.setItem(STORAGE_KEY, String(value));
	} catch {
		/* ignore — best score just won't persist this session */
	}
}

// Light/dark palette picked once — canvas drawing can't use CSS variables.
function getPalette() {
	const dark =
		typeof window !== "undefined" &&
		window.matchMedia &&
		window.matchMedia("(prefers-color-scheme: dark)").matches;
	return dark
		? {
				sky: "#1a2130",
				ground: "#3a4559",
				player: "#63b3ed",
				eye: "#0b1220",
				obsFill: "#3b1f1f",
				obsStroke: "#feb2b2",
				obsLine: "#9b4a4a",
		  }
		: {
				sky: "#f5f8fd",
				ground: "#cbd5e0",
				player: "#1e3799",
				eye: "#fff",
				obsFill: "#fff5f5",
				obsStroke: "#e53e3e",
				obsLine: "#feb2b2",
		  };
}

function createEngine(w, h) {
	const groundY = h * GROUND_RATIO;
	return {
		w,
		h,
		groundY,
		playerY: groundY - PLAYER_SIZE,
		velocityY: 0,
		onGround: true,
		obstacles: [],
		speed: BASE_SPEED,
		elapsed: 0,
		scoreValue: 0,
		spawnTimer: SPAWN_MIN_MS,
		lastTime: null,
		lastDisplayedScore: -1,
		gameOver: false,
	};
}

function rectsOverlap(a, b) {
	return (
		a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y
	);
}

function stepEngine(st, dt) {
	st.elapsed += dt;
	st.speed = BASE_SPEED + SPEED_RAMP * st.elapsed;
	st.scoreValue += dt * SCORE_PER_SEC;

	// gravity + vertical motion
	st.velocityY += GRAVITY * dt;
	st.playerY += st.velocityY * dt;
	const floorY = st.groundY - PLAYER_SIZE;
	if (st.playerY >= floorY) {
		st.playerY = floorY;
		st.velocityY = 0;
		st.onGround = true;
	}

	// spawn new obstacles
	st.spawnTimer -= dt * 1000;
	if (st.spawnTimer <= 0) {
		const height = 20 + Math.random() * 24;
		const width = 16 + Math.random() * 14;
		st.obstacles.push({
			x: st.w + width,
			y: st.groundY - height,
			w: width,
			h: height,
		});
		st.spawnTimer = SPAWN_MIN_MS + Math.random() * (SPAWN_MAX_MS - SPAWN_MIN_MS);
	}

	// move + cull obstacles, check collision
	const playerX = st.w * 0.16;
	const playerRect = { x: playerX, y: st.playerY, w: PLAYER_SIZE, h: PLAYER_SIZE };
	st.obstacles = st.obstacles.filter((o) => {
		o.x -= st.speed * dt;
		return o.x + o.w > -4;
	});
	for (let i = 0; i < st.obstacles.length; i++) {
		if (rectsOverlap(playerRect, st.obstacles[i])) {
			st.gameOver = true;
			break;
		}
	}
}

function roundRect(ctx, x, y, w, h, r) {
	ctx.beginPath();
	ctx.moveTo(x + r, y);
	ctx.arcTo(x + w, y, x + w, y + h, r);
	ctx.arcTo(x + w, y + h, x, y + h, r);
	ctx.arcTo(x, y + h, x, y, r);
	ctx.arcTo(x, y, x + w, y, r);
	ctx.closePath();
}

function drawFrame(ctx, st, palette) {
	const { w, h, groundY, playerY } = st;
	ctx.clearRect(0, 0, w, h);

	ctx.fillStyle = palette.sky;
	ctx.fillRect(0, 0, w, h);

	ctx.strokeStyle = palette.ground;
	ctx.lineWidth = 2;
	ctx.beginPath();
	ctx.moveTo(0, groundY + 1);
	ctx.lineTo(w, groundY + 1);
	ctx.stroke();

	// obstacles — little stacks of "duplicate" lead cards
	st.obstacles.forEach((o) => {
		ctx.fillStyle = palette.obsFill;
		ctx.strokeStyle = palette.obsStroke;
		ctx.lineWidth = 1.5;
		roundRect(ctx, o.x, o.y, o.w, o.h, 3);
		ctx.fill();
		ctx.stroke();

		ctx.strokeStyle = palette.obsLine;
		ctx.lineWidth = 1;
		for (let i = 1; i <= 2; i++) {
			const ly = o.y + (o.h * i) / 3;
			ctx.beginPath();
			ctx.moveTo(o.x + 2, ly);
			ctx.lineTo(o.x + o.w - 2, ly);
			ctx.stroke();
		}
	});

	// player — a friendly little "lead" on the run
	const playerX = w * 0.16;
	ctx.fillStyle = palette.player;
	roundRect(ctx, playerX, playerY, PLAYER_SIZE, PLAYER_SIZE, 6);
	ctx.fill();
	ctx.fillStyle = palette.eye;
	ctx.beginPath();
	ctx.arc(playerX + PLAYER_SIZE * 0.35, playerY + PLAYER_SIZE * 0.4, 2, 0, Math.PI * 2);
	ctx.arc(playerX + PLAYER_SIZE * 0.68, playerY + PLAYER_SIZE * 0.4, 2, 0, Math.PI * 2);
	ctx.fill();
}

export default function RunnerGame() {
	const containerRef = useRef(null);
	const canvasRef = useRef(null);
	const rafRef = useRef(null);
	const stateRef = useRef(null);
	const paletteRef = useRef(getPalette());

	const [phase, setPhase] = useState("idle"); // idle | playing | paused | over
	const [score, setScore] = useState(0);
	const [best, setBest] = useState(loadBest);

	// ---- size the canvas backing store to match its CSS box ---------------
	useEffect(() => {
		const canvas = canvasRef.current;
		const container = containerRef.current;
		if (!canvas || !container) return undefined;

		const applySize = () => {
			const rect = container.getBoundingClientRect();
			const dpr = window.devicePixelRatio || 1;
			const cssWidth = Math.max(240, Math.round(rect.width || 240));
			const cssHeight = Math.round(cssWidth * 0.42);
			canvas.style.width = `${cssWidth}px`;
			canvas.style.height = `${cssHeight}px`;
			canvas.width = Math.round(cssWidth * dpr);
			canvas.height = Math.round(cssHeight * dpr);
			const ctx = canvas.getContext("2d");
			if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
		};

		applySize();

		if (typeof ResizeObserver === "function") {
			const ro = new ResizeObserver(applySize);
			ro.observe(container);
			return () => ro.disconnect();
		}
		window.addEventListener("resize", applySize);
		return () => window.removeEventListener("resize", applySize);
	}, []);

	// ---- pause the loop whenever the tab isn't visible ---------------------
	useEffect(() => {
		const onVisibilityChange = () => {
			if (document.hidden) {
				setPhase((p) => {
					if (p !== "playing") return p;
					if (stateRef.current) stateRef.current.lastTime = null;
					return "paused";
				});
			} else {
				setPhase((p) => (p === "paused" ? "playing" : p));
			}
		};
		document.addEventListener("visibilitychange", onVisibilityChange);
		return () => document.removeEventListener("visibilitychange", onVisibilityChange);
	}, []);

	// ---- the loop itself, only alive while actually playing ---------------
	useEffect(() => {
		if (phase !== "playing") return undefined;
		const canvas = canvasRef.current;
		const ctx = canvas && canvas.getContext("2d");
		if (!ctx) return undefined;

		const frame = (ts) => {
			const st = stateRef.current;
			if (!st) return;
			if (st.lastTime == null) st.lastTime = ts;
			const dt = Math.min(0.05, Math.max(0, (ts - st.lastTime) / 1000));
			st.lastTime = ts;

			stepEngine(st, dt);
			drawFrame(ctx, st, paletteRef.current);

			if (st.gameOver) {
				const finalScore = Math.floor(st.scoreValue);
				setScore(finalScore);
				setPhase("over");
				setBest((prevBest) => {
					if (finalScore > prevBest) {
						saveBest(finalScore);
						return finalScore;
					}
					return prevBest;
				});
				return;
			}

			const flo = Math.floor(st.scoreValue);
			if (flo !== st.lastDisplayedScore) {
				st.lastDisplayedScore = flo;
				setScore(flo);
			}
			rafRef.current = requestAnimationFrame(frame);
		};

		rafRef.current = requestAnimationFrame(frame);
		return () => {
			if (rafRef.current) cancelAnimationFrame(rafRef.current);
			rafRef.current = null;
		};
	}, [phase]);

	const startGame = () => {
		const canvas = canvasRef.current;
		if (!canvas) return;
		stateRef.current = createEngine(canvas.clientWidth, canvas.clientHeight);
		setScore(0);
		setPhase("playing");
	};

	// Jump is also how the game starts (idle) and restarts (game over) — a
	// single control does triple duty, and every path is a user gesture, so
	// nothing here ever auto-starts the animation.
	const jump = () => {
		if (phase === "idle" || phase === "over") {
			startGame();
			return;
		}
		if (phase !== "playing") return;
		const st = stateRef.current;
		if (st && st.onGround) {
			st.velocityY = JUMP_VELOCITY;
			st.onGround = false;
		}
	};

	const handleKeyDown = (e) => {
		const isJumpKey =
			e.code === "Space" ||
			e.key === " " ||
			e.key === "Spacebar" ||
			e.code === "ArrowUp" ||
			e.key === "ArrowUp";
		if (!isJumpKey) return; // everything else (incl. Tab) passes through untouched
		e.preventDefault();
		jump();
	};

	return (
		<div
			ref={containerRef}
			className="arcadeRunner"
			tabIndex={0}
			aria-label="Duplicate Dash, an optional arcade game. Press space, click, or tap to jump."
			onKeyDown={handleKeyDown}
			onPointerDown={jump}>
			<canvas ref={canvasRef} className="arcadeRunnerCanvas" />

			<div className="arcadeRunnerHud" aria-hidden="true">
				<span className="arcadeRunnerScore">Score {score}</span>
				<span className="arcadeRunnerBest">Best {best}</span>
			</div>

			{phase === "idle" && (
				<div className="arcadeRunnerOverlay">
					<p className="arcadeRunnerOverlayTitle">Duplicate Dash</p>
					<p className="arcadeRunnerOverlayHint">
						Space, ↑, click, or tap to jump the duplicates
					</p>
				</div>
			)}

			{phase === "paused" && (
				<div className="arcadeRunnerOverlay">
					<p className="arcadeRunnerOverlayTitle">Paused</p>
					<p className="arcadeRunnerOverlayHint">Come back to this tab to keep going</p>
				</div>
			)}

			{phase === "over" && (
				<div className="arcadeRunnerOverlay">
					<p className="arcadeRunnerOverlayTitle">Busted by a duplicate!</p>
					<p className="arcadeRunnerOverlayHint">
						Score {score} · Best {best} — space, click, or tap to try again
					</p>
				</div>
			)}
		</div>
	);
}
